import * as XLSX from 'xlsx';

// ═══════════════════════════════════════════
// Export Format Types
// ═══════════════════════════════════════════

export interface ExportColumn {
  name: string;
  type: string;
}

export type ExportFormat = 'csv' | 'tsv' | 'json' | 'sql' | 'markdown' | 'html' | 'xml' | 'pretty' | 'excel';

export interface ExportOptions {
  format: ExportFormat;
  // CSV/TSV
  delimiter?: string;
  quoteChar?: string;
  includeHeaders?: boolean;
  nullDisplay?: string;
  // JSON
  jsonPretty?: boolean;
  jsonStyle?: 'array' | 'jsonl';
  // SQL
  sqlStyle?: 'insert' | 'insert-multi' | 'update' | 'upsert';
  sqlTableName?: string;
  sqlIncludeDDL?: boolean;
  sqlBatchSize?: number;
  // XML
  xmlRootElement?: string;
  xmlRowElement?: string;
  xmlPretty?: boolean;
  // HTML
  htmlIncludeStyling?: boolean;
  // Pretty
  prettyBoxStyle?: 'ascii' | 'unicode' | 'simple';
  prettyMaxWidth?: number;
}

// ═══════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════

function escapeField(
  value: unknown,
  quoteChar: string,
  nullDisplay: string,
): string {
  if (value === null || value === undefined) return nullDisplay;
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (!quoteChar) return str;
  // Always quote when the value contains the delimiter, quote char, or newlines
  const escaped = str.replace(
    new RegExp(escapeRegex(quoteChar), 'g'),
    quoteChar + quoteChar,
  );
  return quoteChar + escaped + quoteChar;
}

function needsQuoting(value: string, delimiter: string, quoteChar: string): boolean {
  return (
    value.includes(delimiter) ||
    value.includes(quoteChar) ||
    value.includes('\n') ||
    value.includes('\r')
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeSQLIdentifier(name: string): string {
  return '"' + name.replace(/"/g, '""') + '"';
}

function formatSQLValue(value: unknown): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return "'" + json.replace(/'/g, "''") + "'";
  }
  return "'" + String(value).replace(/'/g, "''") + "'";
}

function escapeXML(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function escapeHTML(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cellToString(value: unknown, nullDisplay: string): string {
  if (value === null || value === undefined) return nullDisplay;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function truncate(str: string, maxWidth: number): string {
  if (str.length <= maxWidth) return str;
  return str.slice(0, maxWidth - 1) + '\u2026';
}

// ═══════════════════════════════════════════
// CSV Formatter
// ═══════════════════════════════════════════

export function formatCSV(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  opts?: Partial<ExportOptions>,
): string {
  const delimiter = opts?.delimiter ?? ',';
  const quoteChar = opts?.quoteChar ?? '"';
  const includeHeaders = opts?.includeHeaders !== false;
  const nullDisplay = opts?.nullDisplay ?? '';

  const formatCell = (value: unknown): string => {
    const str = cellToString(value, nullDisplay);
    if (!quoteChar) return str;
    if (needsQuoting(str, delimiter, quoteChar)) {
      return escapeField(value, quoteChar, nullDisplay);
    }
    return str;
  };

  const lines: string[] = [];

  if (includeHeaders) {
    lines.push(columns.map((col) => formatCell(col.name)).join(delimiter));
  }

  for (const row of rows) {
    lines.push(columns.map((col) => formatCell(row[col.name])).join(delimiter));
  }

  return lines.join('\n');
}

// ═══════════════════════════════════════════
// JSON Formatter
// ═══════════════════════════════════════════

export function formatJSON(
  rows: Record<string, unknown>[],
  opts?: Partial<ExportOptions>,
): string {
  const pretty = opts?.jsonPretty !== false;
  const style = opts?.jsonStyle ?? 'array';

  if (style === 'jsonl') {
    return rows.map((row) => JSON.stringify(row)).join('\n');
  }

  return pretty ? JSON.stringify(rows, null, 2) : JSON.stringify(rows);
}

// ═══════════════════════════════════════════
// SQL Formatter
// ═══════════════════════════════════════════

export function formatSQL(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  opts?: Partial<ExportOptions>,
): string {
  const style = opts?.sqlStyle ?? 'insert';
  const tableName = opts?.sqlTableName ?? 'table_name';
  const batchSize = opts?.sqlBatchSize ?? 100;
  const includeDDL = opts?.sqlIncludeDDL ?? false;

  const quotedTable = escapeSQLIdentifier(tableName);
  const colList = columns.map((col) => escapeSQLIdentifier(col.name)).join(', ');
  const lines: string[] = [];

  // Optional DDL
  if (includeDDL) {
    const ddlLines = columns.map((col) => {
      return `  ${escapeSQLIdentifier(col.name)} ${col.type || 'TEXT'}`;
    });
    lines.push(`CREATE TABLE ${quotedTable} (`);
    lines.push(ddlLines.join(',\n'));
    lines.push(');\n');
  }

  if (style === 'insert') {
    for (const row of rows) {
      const values = columns.map((col) => formatSQLValue(row[col.name])).join(', ');
      lines.push(`INSERT INTO ${quotedTable} (${colList}) VALUES (${values});`);
    }
  } else if (style === 'insert-multi') {
    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
      const valueRows = batch.map((row) => {
        const values = columns.map((col) => formatSQLValue(row[col.name])).join(', ');
        return `  (${values})`;
      });
      lines.push(`INSERT INTO ${quotedTable} (${colList}) VALUES`);
      lines.push(valueRows.join(',\n') + ';');
      if (i + batchSize < rows.length) lines.push('');
    }
  } else if (style === 'update') {
    // UPDATE assumes the first column is the PK. Export has no schema access
    // (ExportColumn is just name + type), so that assumption cannot be checked
    // here — say so in the output rather than let someone run it blind. If the
    // column is not unique, each statement rewrites every matching row.
    const pkCol = columns[0];
    const dataCols = columns.slice(1);
    lines.push(`-- WARNING: these statements assume ${escapeSQLIdentifier(pkCol.name)} uniquely identifies a row.`);
    lines.push('-- DBGo cannot verify that on export. Check it before running this script.');
    lines.push('');
    for (const row of rows) {
      if (dataCols.length === 0) continue;
      const setClauses = dataCols
        .map((col) => `${escapeSQLIdentifier(col.name)} = ${formatSQLValue(row[col.name])}`)
        .join(', ');
      const whereClause = `${escapeSQLIdentifier(pkCol.name)} = ${formatSQLValue(row[pkCol.name])}`;
      lines.push(`UPDATE ${quotedTable} SET ${setClauses} WHERE ${whereClause};`);
    }
  } else if (style === 'upsert') {
    // PostgreSQL-style INSERT ... ON CONFLICT. Same unverifiable assumption as
    // 'update' above — ON CONFLICT needs a real unique constraint on this
    // column or the statement errors at runtime.
    const pkCol = columns[0];
    const dataCols = columns.slice(1);
    lines.push(`-- WARNING: these statements assume ${escapeSQLIdentifier(pkCol.name)} has a unique constraint.`);
    lines.push('-- DBGo cannot verify that on export. Check it before running this script.');
    lines.push('');
    for (const row of rows) {
      const values = columns.map((col) => formatSQLValue(row[col.name])).join(', ');
      const updates = dataCols
        .map((col) => `${escapeSQLIdentifier(col.name)} = EXCLUDED.${escapeSQLIdentifier(col.name)}`)
        .join(', ');
      lines.push(
        `INSERT INTO ${quotedTable} (${colList}) VALUES (${values}) ON CONFLICT (${escapeSQLIdentifier(pkCol.name)}) DO UPDATE SET ${updates};`,
      );
    }
  }

  return lines.join('\n');
}

// ═══════════════════════════════════════════
// Markdown Formatter
// ═══════════════════════════════════════════

export function formatMarkdown(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
): string {
  const nullDisplay = '';
  const header = '| ' + columns.map((col) => col.name).join(' | ') + ' |';
  const separator =
    '| ' +
    columns.map((col) => {
      // Right-align numbers
      const isNumeric = /int|float|double|decimal|numeric|real|serial|bigint|smallint/i.test(col.type);
      return isNumeric ? '---:' : '---';
    }).join(' | ') +
    ' |';
  const body = rows
    .map((row) => {
      const cells = columns.map((col) => {
        const val = cellToString(row[col.name], nullDisplay);
        // Escape pipes in markdown
        return val.replace(/\|/g, '\\|');
      });
      return '| ' + cells.join(' | ') + ' |';
    })
    .join('\n');

  return [header, separator, body].join('\n');
}

// ═══════════════════════════════════════════
// HTML Formatter
// ═══════════════════════════════════════════

export function formatHTML(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  opts?: Partial<ExportOptions>,
): string {
  const includeStyle = opts?.htmlIncludeStyling !== false;
  const nullDisplay = '';

  const style = includeStyle
    ? `<style>
  table { border-collapse: collapse; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px; }
  th, td { border: 1px solid #ddd; padding: 6px 12px; text-align: left; }
  th { background: #f5f5f5; font-weight: 600; }
  tr:nth-child(even) { background: #fafafa; }
  tr:hover { background: #f0f0f0; }
  .null { color: #999; font-style: italic; }
  .number { text-align: right; }
</style>\n`
    : '';

  const headerCells = columns
    .map((col) => `    <th>${escapeHTML(col.name)}</th>`)
    .join('\n');

  const bodyRows = rows
    .map((row) => {
      const cells = columns
        .map((col) => {
          const val = row[col.name];
          if (val === null || val === undefined) {
            return '    <td class="null">NULL</td>';
          }
          const isNumeric = typeof val === 'number';
          const cls = isNumeric ? ' class="number"' : '';
          return `    <td${cls}>${escapeHTML(cellToString(val, nullDisplay))}</td>`;
        })
        .join('\n');
      return `  <tr>\n${cells}\n  </tr>`;
    })
    .join('\n');

  return `${style}<table>
  <thead>
  <tr>
${headerCells}
  </tr>
  </thead>
  <tbody>
${bodyRows}
  </tbody>
</table>`;
}

// ═══════════════════════════════════════════
// XML Formatter
// ═══════════════════════════════════════════

export function formatXML(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  opts?: Partial<ExportOptions>,
): string {
  const rootElement = opts?.xmlRootElement ?? 'data';
  const rowElement = opts?.xmlRowElement ?? 'row';
  const pretty = opts?.xmlPretty !== false;
  const indent = pretty ? '  ' : '';
  const nl = pretty ? '\n' : '';
  const nullDisplay = '';

  const lines: string[] = [];
  lines.push(`<?xml version="1.0" encoding="UTF-8"?>`);
  lines.push(`<${rootElement}>`);

  for (const row of rows) {
    lines.push(`${indent}<${rowElement}>`);
    for (const col of columns) {
      const val = row[col.name];
      if (val === null || val === undefined) {
        lines.push(`${indent}${indent}<${col.name} xsi:nil="true" />`);
      } else {
        const str = cellToString(val, nullDisplay);
        lines.push(`${indent}${indent}<${col.name}>${escapeXML(str)}</${col.name}>`);
      }
    }
    lines.push(`${indent}</${rowElement}>`);
  }

  lines.push(`</${rootElement}>`);
  return lines.join(nl);
}

// ═══════════════════════════════════════════
// Pretty (Box Drawing) Formatter
// ═══════════════════════════════════════════

interface BoxChars {
  topLeft: string;
  topRight: string;
  bottomLeft: string;
  bottomRight: string;
  horizontal: string;
  vertical: string;
  tDown: string;
  tUp: string;
  tRight: string;
  tLeft: string;
  cross: string;
}

const UNICODE_BOX: BoxChars = {
  topLeft: '\u250C', topRight: '\u2510', bottomLeft: '\u2514', bottomRight: '\u2518',
  horizontal: '\u2500', vertical: '\u2502',
  tDown: '\u252C', tUp: '\u2534', tRight: '\u251C', tLeft: '\u2524', cross: '\u253C',
};

const ASCII_BOX: BoxChars = {
  topLeft: '+', topRight: '+', bottomLeft: '+', bottomRight: '+',
  horizontal: '-', vertical: '|',
  tDown: '+', tUp: '+', tRight: '+', tLeft: '+', cross: '+',
};

export function formatPretty(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  opts?: Partial<ExportOptions>,
): string {
  const boxStyle = opts?.prettyBoxStyle ?? 'unicode';
  const maxWidth = opts?.prettyMaxWidth ?? 50;
  const nullDisplay = 'NULL';

  if (boxStyle === 'simple') {
    // Simple space-padded table, no box drawing
    const colWidths = columns.map((col) => {
      let maxW = col.name.length;
      for (const row of rows) {
        const val = cellToString(row[col.name], nullDisplay);
        maxW = Math.max(maxW, Math.min(val.length, maxWidth));
      }
      return Math.min(maxW, maxWidth);
    });

    const header = columns
      .map((col, i) => truncate(col.name, maxWidth).padEnd(colWidths[i]))
      .join('  ');
    const separator = colWidths.map((w) => '-'.repeat(w)).join('  ');
    const body = rows
      .map((row) =>
        columns
          .map((col, i) =>
            truncate(cellToString(row[col.name], nullDisplay), maxWidth).padEnd(colWidths[i]),
          )
          .join('  '),
      )
      .join('\n');

    return [header, separator, body].join('\n');
  }

  const box = boxStyle === 'ascii' ? ASCII_BOX : UNICODE_BOX;

  // Calculate column widths
  const colWidths = columns.map((col) => {
    let maxW = col.name.length;
    for (const row of rows) {
      const val = cellToString(row[col.name], nullDisplay);
      maxW = Math.max(maxW, Math.min(val.length, maxWidth));
    }
    return Math.min(maxW, maxWidth);
  });

  const makeLine = (left: string, mid: string, right: string, fill: string): string => {
    return left + colWidths.map((w) => fill.repeat(w + 2)).join(mid) + right;
  };

  const makeRow = (values: string[]): string => {
    return (
      box.vertical +
      values
        .map((val, i) => ' ' + truncate(val, maxWidth).padEnd(colWidths[i]) + ' ')
        .join(box.vertical) +
      box.vertical
    );
  };

  const lines: string[] = [];
  lines.push(makeLine(box.topLeft, box.tDown, box.topRight, box.horizontal));
  lines.push(makeRow(columns.map((c) => c.name)));
  lines.push(makeLine(box.tRight, box.cross, box.tLeft, box.horizontal));

  for (const row of rows) {
    lines.push(
      makeRow(columns.map((col) => cellToString(row[col.name], nullDisplay))),
    );
  }

  lines.push(makeLine(box.bottomLeft, box.tUp, box.bottomRight, box.horizontal));

  return lines.join('\n');
}

// ═══════════════════════════════════════════
// Excel Formatter
// ═══════════════════════════════════════════

export function formatExcel(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
): Uint8Array {
  const wsData = [
    columns.map(c => c.name),
    ...rows.map(row => columns.map(c => {
      const val = row[c.name];
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val);
      return val;
    })),
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');

  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as Uint8Array;
}

export function triggerExcelDownload(data: Uint8Array, filename: string): void {
  const blob = new Blob([data as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ═══════════════════════════════════════════
// Unified Formatter
// ═══════════════════════════════════════════

export function formatExport(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  options: ExportOptions,
): string {
  switch (options.format) {
    case 'csv':
      return formatCSV(columns, rows, options);
    case 'tsv':
      return formatCSV(columns, rows, { ...options, delimiter: '\t' });
    case 'json':
      return formatJSON(rows, options);
    case 'sql':
      return formatSQL(columns, rows, options);
    case 'markdown':
      return formatMarkdown(columns, rows);
    case 'html':
      return formatHTML(columns, rows, options);
    case 'xml':
      return formatXML(columns, rows, options);
    case 'pretty':
      return formatPretty(columns, rows, options);
    case 'excel':
      return '(Excel export uses binary format)';
    default:
      return formatCSV(columns, rows, options);
  }
}

// ═══════════════════════════════════════════
// File Utilities
// ═══════════════════════════════════════════

export function getFileExtension(format: ExportFormat): string {
  switch (format) {
    case 'csv': return '.csv';
    case 'tsv': return '.tsv';
    case 'json': return '.json';
    case 'sql': return '.sql';
    case 'markdown': return '.md';
    case 'html': return '.html';
    case 'xml': return '.xml';
    case 'pretty': return '.txt';
    case 'excel': return '.xlsx';
  }
}

export function getMimeType(format: ExportFormat): string {
  switch (format) {
    case 'csv': return 'text/csv;charset=utf-8';
    case 'tsv': return 'text/tab-separated-values;charset=utf-8';
    case 'json': return 'application/json;charset=utf-8';
    case 'sql': return 'text/sql;charset=utf-8';
    case 'markdown': return 'text/markdown;charset=utf-8';
    case 'html': return 'text/html;charset=utf-8';
    case 'xml': return 'application/xml;charset=utf-8';
    case 'pretty': return 'text/plain;charset=utf-8';
    case 'excel': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
}

export function triggerDownload(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ═══════════════════════════════════════════
// Legacy convenience exports (for backward compat)
// ═══════════════════════════════════════════

export function exportToCSV(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  filename: string,
): void {
  const content = formatCSV(columns, rows);
  triggerDownload(content, filename, 'text/csv;charset=utf-8');
}

export function exportToJSON(
  rows: Record<string, unknown>[],
  filename: string,
): void {
  const content = formatJSON(rows);
  triggerDownload(content, filename, 'application/json;charset=utf-8');
}

export function exportToSQL(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  tableName: string,
  filename: string,
): void {
  const content = formatSQL(columns, rows, { sqlTableName: tableName });
  triggerDownload(content, filename, 'text/sql;charset=utf-8');
}
